import { spacing, cssSpacing } from "../app/design-system";
import { Typography } from "../../../../../shared/frontend/ui";
import React from "react";
import { summaryListVisibleCount } from "./summaryListVisibleCount";

/**
 * Zweispaltiges Raster (Ronny, 14.10.2026): Die erste Spalte (Datum) ist so breit wie ihr breitester
 * Eintrag in dieser Liste — eine Liste nur mit Tagesdaten rückt näher an den Text als eine mit
 * Uhrzeiten. Die Zeilen übernehmen die Spalten per `subgrid`, damit alle Texte bündig beginnen.
 */
const LIST_CONTAINER_STYLE: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  display: "grid",
  gridTemplateColumns: "max-content minmax(0, 1fr)",
  alignContent: "start",
  columnGap: spacing.md,
};

const LIST_ROW_STYLE: React.CSSProperties = {
  padding: cssSpacing(spacing.xs, 0),
  display: "grid",
  gridTemplateColumns: "subgrid",
  gridColumn: "1 / -1",
  alignItems: "baseline",
  minWidth: 0,
  overflow: "hidden",
};

export type SummaryListContentProps<T> = {
  /** Anzuzeigende Einträge */
  items: T[];
  /** Text, wenn die Liste leer ist */
  emptyText: React.ReactNode;
  /** Max. Anzahl Einträge (Slice vor der Anzeige); optional */
  maxItems?: number;
  /**
   * Zeilen an die verfügbare Höhe anpassen: Es erscheinen so viele Einträge, wie vollständig
   * hineinpassen (mindestens drei), ohne Scrollbalken und ohne angeschnittene Zeile. Der Container
   * muss dafür eine begrenzte Höhe haben (Flex-Kind mit `minHeight: 0`).
   */
  fitToHeight?: boolean;
  /** Eindeutiger Key pro Eintrag */
  getItemKey: (item: T) => string | number;
  /** Einzelzeile pro Eintrag: genau zwei Elemente — Datum, dann Inhalt. */
  renderItem: (item: T) => React.ReactNode;
};

/**
 * Einheitlicher Inhalt für Listen-Karten (kompakte Liste oder Leerzustand).
 * Wird in Card verwendet – z. B. Dashboard (Aufgaben, Leads, Termine, Geburtstage).
 */
export function SummaryListContent<T>({
  items,
  emptyText,
  maxItems,
  fitToHeight = false,
  getItemKey,
  renderItem,
}: SummaryListContentProps<T>) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [measure, setMeasure] = React.useState<{ containerHeight: number; rowHeight: number }>({ containerHeight: 0, rowHeight: 0 });

  React.useLayoutEffect(() => {
    if (!fitToHeight) return;
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const firstRow = el.firstElementChild as HTMLElement | null;
      const rowHeight = firstRow ? firstRow.getBoundingClientRect().height : 0;
      const containerHeight = el.getBoundingClientRect().height;
      setMeasure((prev) =>
        prev.containerHeight === containerHeight && prev.rowHeight === rowHeight ? prev : { containerHeight, rowHeight }
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [fitToHeight, items.length]);

  if (items.length === 0) {
    return <Typography.Text type="secondary">{emptyText}</Typography.Text>;
  }
  const limited = maxItems != null && maxItems > 0 ? items.slice(0, maxItems) : items;
  const dataSource = fitToHeight
    ? limited.slice(0, summaryListVisibleCount(measure.containerHeight, measure.rowHeight, limited.length))
    : limited;
  return (
    <div
      ref={containerRef}
      role="list"
      style={fitToHeight ? { ...LIST_CONTAINER_STYLE, overflow: "hidden" } : LIST_CONTAINER_STYLE}
    >
      {dataSource.map((item) => (
        <div
          key={getItemKey(item)}
          role="listitem"
          style={LIST_ROW_STYLE}
        >
          {renderItem(item)}
        </div>
      ))}
    </div>
  );
}
